import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus, Check } from 'lucide-react';

interface CategoryAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  categories: string[];
  label?: string;
  placeholder?: string;
  required?: boolean;
}

export const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  value,
  onChange,
  categories,
  label,
  placeholder = 'Type to search or add new...',
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredCategories = categories.filter(cat =>
    cat.toLowerCase().includes(inputValue.toLowerCase())
  );

  const showNewOption = inputValue.trim() &&
    !categories.some(cat => cat.toLowerCase() === inputValue.trim().toLowerCase());

  const totalOptions = filteredCategories.length + (showNewOption ? 1 : 0);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (inputValue !== value) {
          setInputValue(value);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, inputValue]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && dropdownRef.current) {
      const highlighted = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelect = (category: string) => {
    onChange(category);
    setInputValue(category);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < totalOptions - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : totalOptions - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          if (highlightedIndex < filteredCategories.length) {
            handleSelect(filteredCategories[highlightedIndex]);
          } else if (showNewOption) {
            handleSelect(inputValue.trim());
          }
        } else if (inputValue.trim()) {
          const exactMatch = filteredCategories.find(
            cat => cat.toLowerCase() === inputValue.trim().toLowerCase()
          );
          handleSelect(exactMatch || inputValue.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setInputValue(value);
        setHighlightedIndex(-1);
        break;
      case 'Tab':
        setIsOpen(false);
        if (inputValue.trim() && inputValue !== value) {
          const exactMatch = filteredCategories.find(
            cat => cat.toLowerCase() === inputValue.trim().toLowerCase()
          );
          onChange(exactMatch || inputValue.trim());
        }
        break;
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          className="input pr-10"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (totalOptions > 0 || inputValue) && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {filteredCategories.length > 0 && (
            <div className="py-1">
              {filteredCategories.map((category, index) => {
                const isHighlighted = index === highlightedIndex;
                const isSelected = category === value;

                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => handleSelect(category)}
                    className={`w-full px-4 py-2.5 text-left flex items-center justify-between transition-colors ${
                      isHighlighted
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="truncate">{category}</span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {showNewOption && (
            <>
              {filteredCategories.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700" />
              )}
              <button
                type="button"
                onClick={() => handleSelect(inputValue.trim())}
                className={`w-full px-4 py-2.5 text-left flex items-center gap-2 transition-colors ${
                  highlightedIndex === filteredCategories.length
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <Plus className="h-4 w-4" />
                <span>
                  Create "<span className="font-medium">{inputValue.trim()}</span>"
                </span>
              </button>
            </>
          )}

          {filteredCategories.length === 0 && !showNewOption && inputValue && (
            <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
              No categories found
            </div>
          )}
        </div>
      )}
    </div>
  );
};
